import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { AIRAMark } from '@/components/brand/Marks';
import { RegistryLink } from '@/components/RegistryLink';
import { PremiumQuoteCard } from '@/components/submission/ScoringResult';
import { SubmissionBrokerageDial } from '@/components/submission/BrokerageDial';
import { MarketPositionCard } from '@/components/MarketPositionCard';
import { ScoreGauge } from '@/components/submission/ScoreGauge';
import { BindCelebration } from '@/components/BindCelebration';
import { NewClientForm } from '@/components/submission/NewClientForm';
import { NextStepCard } from '@/components/submission/NextStepCard';
import SubmissionSteps from '@/components/submission/SubmissionSteps';
import AsyncNarrative from '@/components/submission/AsyncNarrative';
import TurnoverSplitField from '@/components/submission/TurnoverSplitField';
import VehicleScheduleField from '@/components/submission/VehicleScheduleField';
import FamilyPicker from '@/components/submission/FamilyPicker';
import FieldInput from '@/components/submission/FieldInput';
import IndustryQuestions from '@/components/submission/IndustryQuestions';
import ChargesByState from '@/components/submission/ChargesByState';
import CombinedLiabilityResult from '@/components/submission/CombinedLiabilityResult';
import { ResidentialJourney, RESIDENTIAL_PRODUCTS } from '@/components/property/journey/ResidentialJourney';
import {
  Search, Building2,
  ArrowRight, CheckCircle, XCircle, AlertTriangle, Loader2,
  ChevronDown, ChevronUp, BookOpen, Upload, FileSpreadsheet, RefreshCw, FileText
} from 'lucide-react';

const DECISION_STYLES = {
  APPROVE: { bg: '#1D9E75', icon: CheckCircle, label: 'Approved' },
  CONDITIONS: { bg: '#00B4D8', icon: CheckCircle, label: 'Conditions' },
  REFER: { bg: '#E8A020', icon: AlertTriangle, label: 'Referred' },
  DECLINE: { bg: '#E24B4A', icon: XCircle, label: 'Declined' },
};

const fmtK = (v) => v >= 1000 ? `$${(v / 1000).toLocaleString('en-AU')}k` : `$${v}`;

// Track A: Families 1–2 carry industry question sets and the three confirmed charge states.
const COMBINED_ID = 'combined_pi_pl';
const COMBINED_LEGS = ['professional_indemnity', 'public_liability'];
const TRACK_A_CLASSES = ['public_liability', 'professional_indemnity', 'management_liability', COMBINED_ID];

async function loadCombinedConfig(api) {
  const [pi, pl] = await Promise.all(COMBINED_LEGS.map(c => api.get(`/insurance-classes/${c}`).then(r => r.data)));
  const seen = new Set(['annual_turnover']);
  const fields = [...pi.submission_fields, ...pl.submission_fields]
    .filter(f => !seen.has(f.name) && seen.add(f.name))
    .map(f => f.name === 'annual_fees' ? { ...f, label: 'Annual fees / turnover ($) — shared by both legs' } : f);
  return {
    id: COMBINED_ID, name: 'Combined PI + Public Liability', combined: COMBINED_LEGS,
    description: 'One set of answers, two decisions — PI and PL each price, refer and bind on their own terms.',
    submission_fields: fields, section_config: pl.section_config || [],
    refer_triggers: [...(pi.refer_triggers || []), ...(pl.refer_triggers || [])],
    hard_declines: [...(pi.hard_declines || []), ...(pl.hard_declines || [])],
  };
}

function ClassProfilePanel({ k, config }) {
  if (!k) return null;
  return (
    <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #1A4C7C' }} data-testid="class-uw-profile">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-4 h-4" style={{ color: '#1A4C7C' }} />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#1A4C7C' }}>How this class is underwritten</span>
        <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }} data-testid="class-premium-band">
          Typical premium {fmtK(k.premium_band.typical_min)}–{fmtK(k.premium_band.typical_max)}
        </span>
      </div>
      <p className="text-xs leading-relaxed mb-3" style={{ color: '#0D1B2A' }}>{k.treatment}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#5C6B7A' }}>Key rating factors</p>
          {k.key_factors.map(f => <p key={f} className="py-0.5" style={{ color: '#0D1B2A' }}>• {f}</p>)}
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#E8A020' }}>Refer triggers</p>
          {(config?.refer_triggers || []).map(t => <p key={t.desc} className="py-0.5" style={{ color: '#0D1B2A' }}>• {t.desc}</p>)}
          {(config?.hard_declines || []).length > 0 && (
            <p className="mt-1 text-[10px]" style={{ color: '#E24B4A' }}>Hard declines: {(config.hard_declines || []).map(h => h.replace(/_/g, ' ')).join(', ')}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#5C6B7A' }}>Required documents</p>
          {k.required_documents.map(d => <p key={d} className="py-0.5" style={{ color: '#0D1B2A' }}>• {d}</p>)}
        </div>
      </div>
    </Card>
  );
}

export default function ClassSubmissionPage() {
  const api = useApi();
  const { user } = useAuth();
  const isStaff = ['platform_owner', 'mga_admin', 'underwriter'].includes(user?.role);
  const navigate = useNavigate();
  const [classes, setClasses] = useState({});
  const [families, setFamilies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [affinityId, setAffinityId] = useState('');
  const [knowledge, setKnowledge] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [classConfig, setClassConfig] = useState(null);
  const [query, setQuery] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [scoringResult, setScoringResult] = useState(null);
  const [combinedResult, setCombinedResult] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appetite, setAppetite] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const editLogRef = useRef([]);

  const recordEdit = (name, before, after) => {
    if (before === undefined || before === null || before === '' || String(before) === String(after)) return;
    editLogRef.current.push({ field: name, before, after, at: new Date().toISOString() });
  };

  useEffect(() => {
    if (!selectedEntity) { setAppetite(null); return; }
    api.get(`/classes/appetite?entity_id=${selectedEntity.entity_id}`)
      .then(r => setAppetite(r.data)).catch(() => setAppetite(null));
  }, [selectedEntity, api]);

  const [searchParams] = useSearchParams();
  const [prefill, setPrefill] = useState(null);   // from an uploaded document: Quote & Bind opens filled in
  useEffect(() => {
    const eid = searchParams.get('entity_id');
    const cid = searchParams.get('class_id');
    const uploadId = searchParams.get('upload');
    if (eid) api.get(`/entities/${eid}`).then(r => setSelectedEntity(r.data)).catch(() => {});
    if (cid) selectClass(cid).catch(() => {});
    if (uploadId) applyUpload(uploadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyUpload = async (uploadId) => {
    try {
      const { data } = await api.get(`/documents/uploads/${uploadId}`);
      const pf = data.prefill || {};
      await selectClass(pf.insurance_class || 'sme_commercial');
      setForm(p => ({ ...p, ...(pf.form || {}) }));
      if (data.entity_match?.entity_id) {
        const ent = await api.get(`/entities/${data.entity_match.entity_id}`);
        setSelectedEntity(ent.data);
      } else {
        setQuery(pf.new_client?.legal_name || '');
        setAddingClient(true);
      }
      setPrefill({ filename: data.filename, missing: pf.missing || [], matched: !!data.entity_match?.entity_id, client: pf.new_client || {},
                   read: pf.read || { mode: 'text' }, lowConfidence: !!pf.low_confidence });
    } catch (err) {
      console.error('Upload prefill error:', err?.message);
      setSubmitError('The uploaded document could not be loaded — pick the client and enter the risk by hand.');
    }
  };

  useEffect(() => {
    // Family-first: six families, variants beneath. A broker receives only what passes the gate.
    api.get("/families").then(r => {
      const fams = r.data.families || [];
      setFamilies(fams);
      setClasses(Object.fromEntries(fams.flatMap(f => f.variants.filter(v => v.quotable).map(v => [v.class_id, v]))));
    }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
    api.get("/classes/knowledge").then(r => setKnowledge(r.data)).catch(() => {});
    if (isStaff) api.get("/affinity/partners").then(r => setPartners((r.data.partners || []).filter(p => p.status === 'active'))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const selectClass = async (classId) => {
    const data = classId === COMBINED_ID ? await loadCombinedConfig(api) : (await api.get(`/insurance-classes/${classId}`)).data;
    setClassConfig(data);
    setSelectedClass(classId);
    setSubmitError('');
    const defaults = {};
    (data.submission_fields || []).forEach(f => { if (f.default !== undefined) defaults[f.name] = f.default; });
    const sc = data.section_config || [];
    if (sc.length) {
      defaults.sections_selected = sc.filter(s => s.default_on).map(s => s.id);
      sc.forEach(s => { if (s.default_on && s.amount_field) defaults[s.amount_field] = s.default_amount; });
    }
    setForm(defaults);
    editLogRef.current = [];
  };

  const searchEntities = async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/entities/search?q=${encodeURIComponent(q)}&limit=8`);
      setResults(data);
    } catch (err) { console.error("API error:", err?.message); }
    setSearching(false);
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(window._csTimeout);
    window._csTimeout = setTimeout(() => searchEntities(val), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Native validation only shows a tooltip on a field that may be off-screen — to a broker that
    // looks like a frozen Quote button. Name the blanks, take them to the first one.
    const blanks = Array.from(e.currentTarget.querySelectorAll(':invalid')).filter(el => el.willValidate);
    if (blanks.length) {
      const names = blanks.map(el => (el.closest('div')?.querySelector('label')?.textContent || el.placeholder || '').replace(/\s*\*\s*$/, '').trim()).filter(Boolean);
      const msg = `${names.length === 1 ? 'One answer is' : `${names.length} answers are`} still needed: ${names.slice(0, 4).join(', ')}${names.length > 4 ? '…' : ''}`;
      setSubmitError(msg);
      toast.error(msg, { id: 'quote-blanks' });
      blanks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      blanks[0].focus({ preventScroll: true });
      return;
    }
    toast.dismiss('quote-blanks');
    if (form.turnover_by_state) {
      const tot = Object.values(form.turnover_by_state).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      if (Math.abs(tot - 100) > 0.01) {
        setSubmitError(`Turnover split must total 100% — currently ${Math.round(tot * 100) / 100}%`);
        return;
      }
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      if (selectedClass === COMBINED_ID) {
        const { data } = await api.post('/submissions/combined-liability', {
          entity_id: selectedEntity.entity_id, submission_data: form,
          edit_events: editLogRef.current.slice(-100), affinity_partner_id: affinityId || undefined,
        });
        setCombinedResult(data);
        setSubmitting(false);
        return;
      }
      const { data } = await api.post('/submissions/class', {
        entity_id: selectedEntity.entity_id,
        insurance_class: selectedClass,
        submission_data: form,
        edit_events: editLogRef.current.slice(-100),
        affinity_partner_id: affinityId || undefined,
      });
      setScoringResult(data);
      setCelebrate(!!data.policy_id);
    setSubmitting(false);
    } catch (err) {
      setSubmitError(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Submission failed — please review the inputs');
      setSubmitting(false);
    }
  };

  const reset = () => { setSelectedClass(null); setClassConfig(null); setSelectedEntity(null); setScoringResult(null); setCombinedResult(null); setForm({}); setSubmitError(''); setAffinityId(''); editLogRef.current = []; };

  const quoteMlCrossSell = async () => {
    const rev = form.annual_turnover, emp = form.employee_count;
    setScoringResult(null);
    setCelebrate(false);
    await selectClass('management_liability');
    setForm(p => ({ ...p, annual_revenue: rev || p.annual_revenue, employee_count: emp || p.employee_count }));
  };

  const quotePaCrossSell = async (persons) => {
    setScoringResult(null);
    setCelebrate(false);
    await selectClass('group_personal_accident');
    setForm(p => ({ ...p, persons_covered: persons || p.persons_covered }));
  };

  if (loading) return <Skeleton className="h-64 rounded-sm" />;

  if (combinedResult) {
    return <CombinedLiabilityResult result={combinedResult} onReset={reset} onOpen={(id) => navigate(`/submissions/${id}`)} />;
  }

  // Family 6 — the residential journey: address → property → (tenancy) → cover → disclosure → price → bind → documents.
  // No entity search: the insured is a person, the address is the key.
  if (RESIDENTIAL_PRODUCTS[selectedClass]) {
    return (
      <div className="space-y-4" data-testid="class-submission-page">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>{classConfig?.name || 'Residential Property'}</h1>
        <ResidentialJourney api={api} classId={selectedClass} onReset={reset} />
      </div>
    );
  }

  // Scoring Result
  if (scoringResult) {
    const sr = scoringResult.scoring_result;
    const returned = scoringResult.status === 'info_requested' && scoringResult.broker_action;
    const ds = returned
      ? { bg: '#E8A020', icon: AlertTriangle, label: scoringResult.broker_action === 'answer' ? 'Back to you — answers needed' : 'Back to you — underwriter needed' }
      : scoringResult.policy_id ? { ...(DECISION_STYLES[sr.decision] || DECISION_STYLES.APPROVE), label: `${(DECISION_STYLES[sr.decision] || DECISION_STYLES.APPROVE).label} & bound` }
      : (DECISION_STYLES[sr.decision] || DECISION_STYLES.REFER);
    const DIcon = ds.icon;
    return (
      <div data-testid="class-scoring-result" className="max-w-3xl mx-auto space-y-5">
        <SubmissionSteps done={[true, true, true, true]} current={-1} />
        <Card className="rounded-sm overflow-hidden border-0">
          <div className="p-5 text-white" style={{ background: ds.bg }}>
            <div className="flex items-center gap-3">
              <DIcon className="w-7 h-7" />
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Sans'" }}>{ds.label}</h2>
                <p className="text-white/80 text-sm">{scoringResult.insurance_class_name}</p>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Score</p><p className="text-xl font-bold font-mono mt-1" style={{ color: ds.bg }}>{sr.composite_score}<span className="text-xs font-normal" style={{ color: '#5C6B7A' }}>/10000</span></p></div>
            <div><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Confidence</p><p className="text-xl font-bold mt-1" style={{ color: '#0D1B2A' }}>{(sr.confidence*100).toFixed(0)}%</p></div>
            <div><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>STP</p><p className="text-xl font-bold mt-1" style={{ color: sr.stp ? '#00B4D8' : '#5C6B7A' }}>{sr.stp ? 'Yes' : 'No'}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Class</p><p className="text-sm font-semibold mt-1" style={{ color: '#0D1B2A' }}>{sr.insurance_class}</p></div>
          </div>
        </Card>

        <Card className="rounded-sm bg-white p-5">
          <ScoreGauge sr={sr} premium={sr.marais_quote?.total_payable} />
        </Card>
        <NextStepCard result={scoringResult} isStaff={isStaff} onUpdated={(next) => { setScoringResult(next); if (next?.policy_id) setCelebrate(true); }} />
        {celebrate && scoringResult.policy_id && (
          <BindCelebration policyId={scoringResult.policy_id} policyNumber={scoringResult.policy_number} seconds={sr.decision_seconds} onClose={() => setCelebrate(false)} />
        )}

        {sr.underinsurance?.flagged && (
          <Card className="p-4 rounded-sm border-l-4 bg-white flex items-start gap-2.5" style={{ borderLeftColor: '#E8A020' }} data-testid="underinsurance-alert">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />
            <div className="text-xs">
              <p className="font-semibold" style={{ color: '#B27516' }}>
                Underinsurance flag — declared sum insured is {sr.underinsurance.adequacy_pct}% of the industry benchmark
              </p>
              <p className="mt-0.5 leading-snug" style={{ color: '#5C6B7A' }}>{sr.underinsurance.message}</p>
            </div>
          </Card>
        )}
        {sr.market_position && <MarketPositionCard mp={sr.market_position} />}
        {sr.marais_quote?.section_premiums?.length > 0 && (
          <SectionBreakdownCard sections={sr.marais_quote.section_premiums} gross={sr.marais_quote.gross_premium} />
        )}
        {sr.pa_cross_sell && selectedClass === 'public_liability' && (
          <Card className="p-4 rounded-sm border-l-4 bg-white flex flex-wrap items-center justify-between gap-3" style={{ borderLeftColor: '#1A4C7C' }} data-testid="pa-cross-sell-card">
            <div>
              <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Clubs and associations usually bundle Group PA with liability</p>
              <p className="text-xs mt-0.5" style={{ color: '#5C6B7A' }}>
                Cover {sr.pa_cross_sell.persons} members/staff for accidents from ~${Math.round(sr.pa_cross_sell.estimated_total).toLocaleString('en-AU')}/yr.
              </p>
            </div>
            <Button data-testid="pa-cross-sell-quote-btn" onClick={() => quotePaCrossSell(sr.pa_cross_sell.persons)} className="rounded-sm text-xs font-semibold" style={{ background: '#1A4C7C' }}>
              Quote Group PA
            </Button>
          </Card>
        )}
        {sr.ml_cross_sell && selectedClass === 'sme_commercial' && (
          <Card className="p-4 rounded-sm border-l-4 bg-white flex flex-wrap items-center justify-between gap-3" style={{ borderLeftColor: '#1A4C7C' }} data-testid="ml-cross-sell-card">
            <div>
              <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Directors carry personal exposure on this business</p>
              <p className="text-xs mt-0.5" style={{ color: '#5C6B7A' }}>
                Add Management Liability (D&O, EPL, statutory fines, tax audit) from ~${Math.round(sr.ml_cross_sell.estimated_total).toLocaleString('en-AU')}/yr at a $2M limit.
              </p>
            </div>
            <Button data-testid="ml-cross-sell-quote-btn" onClick={quoteMlCrossSell} className="rounded-sm text-xs font-semibold" style={{ background: '#1A4C7C' }}>
              Quote Management Liability
            </Button>
          </Card>
        )}
        {sr.marais_quote && <PremiumQuoteCard q={sr.marais_quote} />}
        {sr.marais_quote?.gross_premium != null && !scoringResult.policy_id && scoringResult.submission_id && (
          <SubmissionBrokerageDial api={api} submissionId={scoringResult.submission_id} quote={sr.marais_quote}
            onChange={q => setScoringResult(r => ({ ...r, scoring_result: { ...r.scoring_result, marais_quote: q } }))} />
        )}
        {sr.marais_quote?.gross_premium != null && knowledge[selectedClass] && (() => {
          const b = knowledge[selectedClass].premium_band;
          const g = sr.marais_quote.gross_premium;
          const within = g >= b.typical_min && g <= b.typical_max;
          return (
            <p className="text-xs flex items-center gap-1.5 px-1" data-testid="class-band-chip" style={{ color: within ? '#1D9E75' : '#B27516' }}>
              {within ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              Premium {within ? 'within' : (g > b.typical_max ? 'above' : 'below')} the typical band for this class ({fmtK(b.typical_min)}–{fmtK(b.typical_max)})
            </p>
          );
        })()}

        <AsyncNarrative sr={sr} submissionId={scoringResult.submission_id} />

        {sr.override_rules_triggered?.length > 0 && (
          <Card className="rounded-sm border-l-4 p-4 bg-white" style={{ borderLeftColor: '#E8A020' }}>
            <h3 className="text-xs font-semibold mb-2" style={{ color: '#E8A020' }}>Override Rules</h3>
            {sr.override_rules_triggered.map((r, i) => (
              <p key={i} className="text-xs flex items-center gap-1" style={{ color: '#0D1B2A' }} data-testid={`override-rule-${r.rule || i}`}>
                <AlertTriangle className="w-3 h-3" style={{ color: r.action === 'AUTO_DECLINE' ? '#E24B4A' : '#E8A020' }} />
                {r.industry && <span className="font-mono text-[10px]" style={{ color: '#5C6B7A' }}>{r.rule}</span>} {r.desc}
              </p>
            ))}
          </Card>
        )}

        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <button onClick={() => setShowBreakdown(!showBreakdown)} className="w-full p-4 flex items-center justify-between hover:bg-[#F5F7FA]">
            <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Dimension Breakdown</span>
            {showBreakdown ? <ChevronUp className="w-4 h-4" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5C6B7A' }} />}
          </button>
          {showBreakdown && <div className="px-4 pb-4 space-y-2">
            {Object.entries(sr.dimension_scores || {}).sort((a, b) => (b[1].score || 0) - (a[1].score || 0)).map(([dim, d]) => (
              <div key={dim} className="flex items-center gap-3">
                <span className="w-5 text-[10px] font-mono font-bold" style={{ color: '#1A4C7C' }}>{dim}</span>
                <div className="flex-1">
                  <div className="flex justify-between text-[10px] mb-0.5"><span style={{ color: '#0D1B2A' }}>{d.name}</span><span className="font-mono" style={{ color: '#5C6B7A' }}>{d.score}/{d.max_points}</span></div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
                    <div className="h-full rounded-full" style={{ width: `${(d.score/d.max_points)*100}%`, background: d.risk_factor > 0.6 ? '#E24B4A' : d.risk_factor > 0.35 ? '#E8A020' : '#1D9E75' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>}
        </Card>

        <div className="flex gap-3">
          <Button onClick={reset} className="rounded-sm" style={{ background: '#1A4C7C' }}>New Submission</Button>
          <Button data-testid="class-view-quote-details-btn" variant="outline" onClick={() => navigate(`/submissions/${scoringResult.submission_id}`)} className="rounded-sm">Full Quote Details</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="class-submission-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>
            {selectedClass ? classConfig?.name : 'Insurance Class Submission'}
          </h1>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>
            {selectedClass ? classConfig?.description : 'Pick a family, then a variant — only what the platform can quote is offered'}
          </p>
        </div>
        {selectedClass && !scoringResult && !submitting && (
          <button onClick={() => { setSelectedClass(null); setClassConfig(null); setForm({}); setSubmitError(''); }}
            data-testid="change-class-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold shrink-0 transition-colors hover:bg-[rgba(26,76,124,0.08)]"
            style={{ color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.3)' }}>
            <RefreshCw className="w-3 h-3" /> Change class
          </button>
        )}
      </div>

      <SubmissionSteps
        done={[!!selectedEntity, !!selectedClass, false, false]}
        current={!selectedClass ? 1 : !selectedEntity ? 0 : submitting ? 3 : 2}
      />

      {prefill && !scoringResult && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-sm text-xs" data-testid="upload-prefill-banner"
          style={{ background: prefill.lowConfidence ? 'rgba(232,160,32,0.08)' : 'rgba(0,180,216,0.08)', border: `1px solid ${prefill.lowConfidence ? 'rgba(232,160,32,0.4)' : 'rgba(0,180,216,0.3)'}`, color: '#0D1B2A' }}>
          <FileText className="w-3.5 h-3.5" style={{ color: prefill.lowConfidence ? '#E8A020' : '#00B4D8' }} />
          <span>
            Pre-filled from <b>{prefill.filename}</b>{prefill.read?.mode === 'photo' ? (prefill.read.parts > 1 ? ` (read from ${prefill.read.parts} photos of one form)` : ' (read from a photo)') : prefill.read?.mode === 'scan' ? ` (read from a scanned PDF, ${prefill.read.pages_read} page${prefill.read.pages_read === 1 ? '' : 's'})` : ''} — {prefill.matched ? 'client matched on file' : 'client not on file yet, confirm the details below'}; check every figure before you quote.
          </span>
          {prefill.lowConfidence && (
            <span className="font-semibold" style={{ color: '#B27516' }} data-testid="upload-prefill-low-confidence">Low-confidence read — the image was hard to read.</span>
          )}
          {prefill.missing.length > 0 && (
            <span className="font-semibold" style={{ color: '#B27516' }} data-testid="upload-prefill-missing">
              The document did not say: {prefill.missing.map(m => m.replace(/_/g, ' ')).join(', ')}.
            </span>
          )}
        </div>
      )}

      {/* Class Selection — family first */}
      {!selectedClass && (
        <FamilyPicker families={families} onSelect={selectClass} isStaff={isStaff} />
      )}

      {/* Entity Search */}
      {selectedClass && !selectedEntity && (
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={() => { setSelectedClass(null); setClassConfig(null); }} className="rounded-sm text-xs">Back to Classes</Button>
          <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5C6B7A' }} />
              <Input data-testid="class-search-input" value={query} onChange={handleSearch} placeholder="Search by ABN or business name..." className="pl-10 rounded-sm" />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: '#00B4D8' }} />}
            </div>
          </Card>
          {results.length > 0 && !addingClient && (
            <Card className="rounded-sm border bg-white divide-y divide-[rgba(136,150,168,0.1)]">
              {results.map(e => (
                <button key={e.entity_id} data-testid={`client-result-${e.entity_id}`} onClick={() => setSelectedEntity(e)} className="w-full p-3 flex items-center justify-between hover:bg-[#F5F7FA] text-left">
                  <div>
                    <span className="text-sm font-medium" style={{ color: '#0D1B2A' }}>{e.legal_name}</span>
                    <span className="text-xs ml-2" style={{ color: '#5C6B7A' }}>{[e.city, e.state].filter(Boolean).join(', ') || 'location not on file'} | {e.abn || e.nzbn || 'no ABN'}</span>
                  </div>
                  <ArrowRight className="w-4 h-4" style={{ color: '#5C6B7A' }} />
                </button>
              ))}
            </Card>
          )}
          {!addingClient && (
            <button type="button" data-testid="add-new-client-button" onClick={() => setAddingClient(true)}
              className="w-full p-3 rounded-sm text-sm text-left transition-colors hover:bg-[rgba(26,76,124,0.06)]"
              style={{ color: '#1A4C7C', border: '1px dashed rgba(26,76,124,0.4)' }}>
              {query.length >= 2 && !searching && results.length === 0 ? `"${query}" is not in the register — ` : 'Client not listed? '}
              <span className="font-semibold">Add a new client</span> (a person or a business; ABN optional)
            </button>
          )}
          {addingClient && (
            <NewClientForm initialName={/\d{6,}/.test(query) ? '' : query} initial={prefill?.client} onCancel={() => setAddingClient(false)}
              onCreated={(entity) => { setAddingClient(false); setSelectedEntity(entity); }} />
          )}
        </div>
      )}

      {/* Out of appetite — hard block */}
      {selectedClass && selectedEntity && !scoringResult && classConfig && appetite?.[selectedClass]?.status === 'out_of_appetite' && (
        <OutOfAppetiteCard
          className={classConfig.name}
          entity={selectedEntity}
          reason={appetite[selectedClass].reasons?.[0]}
          alternates={Object.entries(classes).filter(([id]) => appetite?.[id]?.status === 'in_appetite').slice(0, 4)}
          onSwitchClass={selectClass}
          onChangeEntity={() => setSelectedEntity(null)}
        />
      )}

      {/* Submission Form */}
      {selectedClass && selectedEntity && !scoringResult && classConfig && appetite?.[selectedClass]?.status !== 'out_of_appetite' && (
        <form noValidate onSubmit={handleSubmit} className="space-y-5">
          {appetite?.[selectedClass]?.status === 'refer' && (
            <Card className="p-3.5 rounded-sm border-l-4 bg-white flex items-start gap-2.5" style={{ borderLeftColor: '#E8A020' }} data-testid="appetite-refer-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />
              <div className="text-xs">
                <p className="font-semibold" style={{ color: '#B27516' }}>Outside standard appetite — this risk will be referred to an underwriter</p>
                <p className="mt-0.5" style={{ color: '#5C6B7A' }}>{appetite[selectedClass].reasons?.[0]}</p>
              </div>
            </Card>
          )}
          <ClassProfilePanel k={knowledge[selectedClass]} config={classConfig} />
          <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#00B4D8' }}>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" style={{ color: '#1A4C7C' }} />
              <span className="font-semibold text-sm" style={{ color: '#0D1B2A' }}>{selectedEntity.legal_name}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-xs" style={{ color: '#5C6B7A' }}>
              <span><RegistryLink abn={selectedEntity.abn} nzbn={selectedEntity.nzbn} prefix style={{ color: '#5C6B7A' }} /></span>
              <span>{[selectedEntity.city, selectedEntity.state].filter(Boolean).join(', ') || 'location not on file'}</span>
              <span>{selectedEntity.industry_code}</span>
            </div>
            <button type="button" onClick={() => setSelectedEntity(null)} className="text-xs mt-1 hover:underline" style={{ color: '#1A4C7C' }}>Change</button>
          </Card>

          {(classConfig.section_config || []).length > 0 && (
            <SectionPicker config={classConfig.section_config} form={form} setForm={setForm} />
          )}

          {TRACK_A_CLASSES.includes(selectedClass) && (
            <IndustryQuestions api={api} classIds={classConfig.combined || [selectedClass]} entityId={selectedEntity?.entity_id}
              form={form} setForm={setForm} recordEdit={recordEdit} />
          )}

          {['heavy_motor', 'commercial_motor', 'plant_equipment'].includes(selectedClass) && (
            <ScheduleUpload classId={selectedClass} setForm={setForm} api={api} entityId={selectedEntity?.entity_id}
              appetite={appetite} onSwitchClass={selectClass} />
          )}

          {['commercial_motor', 'heavy_motor'].includes(selectedClass) && (
            <VehicleScheduleField classId={selectedClass} form={form} setForm={setForm}
              homeState={selectedEntity?.state} onSwitchClass={selectClass} />
          )}

          <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>{classConfig.name} Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(classConfig.submission_fields || []).map(field => (
                <FieldInput key={field.name} field={field} form={form} setForm={setForm} recordEdit={recordEdit} />
              ))}
              {classConfig.excess_options && (
                <div>
                  <Label className="text-xs" style={{ color: '#5C6B7A' }}>Excess / Deductible</Label>
                  <select value={form.excess ?? classConfig.excess_options.standard} data-testid="class-field-excess"
                    onChange={e => { recordEdit('excess', form.excess, parseFloat(e.target.value)); setForm(p => ({ ...p, excess: parseFloat(e.target.value) })); }}
                    className="mt-1 w-full px-3 py-2 border border-input rounded-sm text-sm bg-white">
                    {classConfig.excess_options.options.map(o => (
                      <option key={o.amount} value={o.amount}>
                        {`$${o.amount.toLocaleString()}${o.amount === classConfig.excess_options.standard ? ' (standard)' : o.relativity < 1 ? ` (−${Math.round((1 - o.relativity) * 100)}% premium)` : ` (+${Math.round((o.relativity - 1) * 100)}% premium)`}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card>

          {(() => {
            const MOTOR = ['commercial_motor', 'heavy_motor', 'motor_trade', 'personal_motor'];
            const isMotorCls = MOTOR.includes(selectedClass);
            const hasTurnover = (classConfig.submission_fields || []).some(f => ['annual_turnover', 'annual_revenue', 'turnover', 'annual_fees'].includes(f.name));
            return (hasTurnover || isMotorCls) && (
              <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #1A4C7C' }}>
                <TurnoverSplitField value={form.turnover_by_state}
                  onChange={v => setForm(p => ({ ...p, turnover_by_state: v }))}
                  homeState={selectedEntity?.state}
                  basis={isMotorCls ? 'garaging' : 'turnover'} />
              </Card>
            );
          })()}

          {TRACK_A_CLASSES.includes(selectedClass) && (
            <ChargesByState api={api} classId={selectedClass === COMBINED_ID ? 'public_liability' : selectedClass} homeState={selectedEntity?.state} />
          )}

          <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #E8A020' }} data-testid="disclosure-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Known circumstances</p>
                <p className="text-[11px] mt-0.5" style={{ color: '#5C6B7A' }}>Is the applicant aware of any circumstance, incident or complaint that may give rise to a claim? Disclosing it does not decline the risk — it refers to an underwriter, and binding is blocked until they resolve it.</p>
              </div>
              <Switch checked={!!form.known_circumstances} onCheckedChange={v => setForm(p => ({ ...p, known_circumstances: v }))} data-testid="known-circumstances-switch" />
            </div>
            {form.known_circumstances && (
              <textarea value={form.circumstances_detail || ''} onChange={e => setForm(p => ({ ...p, circumstances_detail: e.target.value }))}
                data-testid="circumstances-detail-input" rows={3}
                placeholder="Describe the circumstance in the applicant's own words — what happened, when, who is involved. This exact text is what the underwriter resolves against, and what GEN-E-001 excludes if the outcome is Excluded."
                className="mt-3 w-full px-3 py-2 border border-input rounded-sm text-xs bg-white" />
            )}
          </Card>

          {isStaff && partners.length > 0 && (
            <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #6C4BB5' }} data-testid="channel-picker-card">
              <Label className="text-xs" style={{ color: '#5C6B7A' }}>Distribution channel</Label>
              <select value={affinityId} onChange={e => setAffinityId(e.target.value)} data-testid="channel-partner-select"
                className="mt-1 w-full md:w-96 px-3 py-2 border border-input rounded-sm text-sm bg-white">
                <option value="">Broker channel (default)</option>
                {partners.map(p => (
                  <option key={p.partner_id} value={p.partner_id}>{`Affinity — ${p.name} (${p.commission_pct}% commission)`}</option>
                ))}
              </select>
              {affinityId && <p className="text-[11px] mt-1.5" style={{ color: '#6C4BB5' }}>Labelled affinity channel — the partner's agreement commission is stamped deterministically at bind. Lines outside the agreement are blocked at intake.</p>}
            </Card>
          )}

          {submitError && (
            <Card className="p-3.5 rounded-sm border-l-4 bg-white flex items-start gap-2.5" style={{ borderLeftColor: '#E24B4A' }} data-testid="submit-error-banner">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E24B4A' }} />
              <p className="text-xs" style={{ color: '#B03230' }}>{submitError.replace(/^OUT_OF_APPETITE:\s*/, '')}</p>
            </Card>
          )}

          <div className="flex gap-3">
            <Button data-testid="class-submit-btn" type="submit"
              disabled={submitting || ((classConfig.section_config || []).length > 0 && !(form.sections_selected || []).length)}
              className="rounded-sm font-semibold px-8" style={{ background: '#1A4C7C', height: 44 }}>
              {submitting ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Scoring...</span> : 'Submit for Fraime Scoring'}
            </Button>
            <Button type="button" variant="outline" onClick={reset} className="rounded-sm">Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}

const fmt$ = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString('en-AU')}`;

function ScheduleUpload({ classId, setForm, api, onSwitchClass, entityId }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(false);
  const [declBusy, setDeclBusy] = useState(false);
  const inputRef = React.useRef(null);
  const VEHICLE_CATS = ['truck', 'prime_mover', 'trailer', 'van', 'ute', 'car'];

  const downloadFleetDeclaration = async () => {
    setDeclBusy(true);
    try {
      const vehicles = (result?.items || [])
        .filter(i => VEHICLE_CATS.includes(i.category || ''))
        .map(i => ({ description: i.description, year: i.year, category: i.category, value: i.value }));
      const resp = await api.post('/documents/fleet-declaration/prefill',
        { entity_id: entityId || '', vehicles }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Fleet_Schedule_Garaging_Declaration.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Could not generate the fleet declaration'); }
    setDeclBusy(false);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setError(''); setResult(null); setApplied(false);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/schedules/extract', fd);
      setResult(data);
    } catch (err) {
      setError(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Extraction failed — try a .xlsx or .csv schedule');
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const apply = () => {
    const sf = result?.suggested_fields || {};
    const allowed = classId === 'plant_equipment'
      ? ['plant_value', 'plant_type', 'plant_age_years']
      : ['fleet_size', 'total_vehicle_value', 'gvm_band'];
    const patch = {};
    allowed.forEach(k => { if (sf[k] !== undefined) patch[k] = sf[k]; });
    if (classId !== 'plant_equipment') {
      const TYPE_MAP = { prime_mover: 'prime_mover', trailer: 'semi_trailer', van: 'van_light', ute: 'dual_cab_ute', car: 'company_car' };
      const vehicles = (result?.items || [])
        .filter(i => VEHICLE_CATS.includes(i.category || ''))
        .map(i => ({
          rego: i.rego || '', description: i.description || '', year: i.year || '',
          type: i.category === 'truck' ? ((i.gvm_tonnes || 0) > 4.5 ? 'rigid_4_5_12t' : 'light_rigid') : (TYPE_MAP[i.category] || 'van_light'),
          value: i.value || 0, gvm_tonnes: i.gvm_tonnes || undefined,
          state: i.state || undefined, driver: 'clean_25_65',
        }));
      if (vehicles.length) patch.vehicles = vehicles;
    }
    setForm(p => ({ ...p, ...patch }));
    setApplied(true);
  };

  return (
    <Card className="p-4 rounded-sm border border-dashed bg-white space-y-3" style={{ borderColor: 'rgba(26,76,124,0.35)' }} data-testid="schedule-upload-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <FileSpreadsheet className="w-5 h-5" style={{ color: '#1A4C7C' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Have a {classId === 'plant_equipment' ? 'plant' : 'fleet'} schedule?</p>
            <p className="text-[11px]" style={{ color: '#5C6B7A' }}>Upload the spreadsheet (.xlsx / .csv) — AI extracts the items and fills the quote for you.</p>
          </div>
        </div>
        <input ref={inputRef} type="file" accept=".xlsx,.csv,.txt,.pdf" className="hidden" onChange={onFile} data-testid="schedule-upload-input" />
        <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="schedule-upload-btn"
          className="rounded-sm text-xs font-semibold" style={{ borderColor: 'rgba(26,76,124,0.35)', color: '#1A4C7C' }}>
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Extracting…</> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload schedule</>}
        </Button>
      </div>
      {error && <p className="text-xs" style={{ color: '#E24B4A' }} data-testid="schedule-upload-error">{error}</p>}
      {result && (
        <div className="rounded-sm p-3 space-y-2" style={{ background: 'rgba(26,76,124,0.05)' }} data-testid="schedule-result-summary">
          <p className="text-xs font-semibold" style={{ color: '#0D1B2A' }}>
            {result.item_count} items extracted from {result.filename} · total value {fmt$(result.total_value)} · {result.schedule_type} schedule
          </p>
          <div className="space-y-0.5">
            {result.items.slice(0, 4).map((it, i) => (
              <p key={i} className="text-[11px] font-mono" style={{ color: '#5C6B7A' }}>
                {it.description}{it.year ? ` (${it.year})` : ''}{it.gvm_tonnes ? ` · ${it.gvm_tonnes}t GVM` : ''} — {fmt$(it.value)}
              </p>
            ))}
            {result.item_count > 4 && <p className="text-[11px]" style={{ color: '#64748B' }}>…and {result.item_count - 4} more</p>}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" onClick={apply} data-testid="schedule-apply-btn"
              className="rounded-sm text-xs font-semibold h-8" style={{ background: applied ? '#1D9E75' : '#1A4C7C' }}>
              {applied ? 'Applied to quote ✓' : 'Apply to quote'}
            </Button>
            {classId !== 'plant_equipment' && (result.items || []).some(i => ['truck', 'prime_mover', 'trailer', 'van', 'ute', 'car'].includes(i.category || '')) && (
              <Button type="button" variant="outline" onClick={downloadFleetDeclaration} disabled={declBusy} data-testid="schedule-fleet-declaration-btn"
                className="rounded-sm text-xs font-semibold h-8" style={{ borderColor: 'rgba(26,76,124,0.35)', color: '#1A4C7C' }}
                title="Pre-filled Fleet Schedule & Garaging Declaration from this upload — for client signature">
                {declBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Fleet Declaration (PDF)
              </Button>
            )}
            {result.suggested_class && result.suggested_class !== classId && (
              <button type="button" onClick={() => onSwitchClass(result.suggested_class)} data-testid="schedule-switch-class-btn"
                className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ color: '#B27516', border: '1px solid rgba(232,160,32,0.4)' }}>
                This schedule looks like {result.suggested_class.replace(/_/g, ' ')} — switch class
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function SectionPicker({ config, form, setForm }) {
  const selected = form.sections_selected || [];
  const toggle = (s) => setForm(p => {
    const cur = p.sections_selected || [];
    const on = cur.includes(s.id);
    const next = { ...p, sections_selected: on ? cur.filter(x => x !== s.id) : [...cur, s.id] };
    if (!on && s.amount_field && !next[s.amount_field]) next[s.amount_field] = s.default_amount;
    return next;
  });
  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3" data-testid="section-picker">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Cover Sections</h3>
        <p className="text-xs mt-0.5" style={{ color: '#5C6B7A' }}>Select the sections to include — each section is rated separately. {selected.length} of {config.length} selected.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {config.map(s => {
          const on = selected.includes(s.id);
          return (
            <div key={s.id} className={`p-3 rounded-sm border transition-all ${on ? 'border-[#00B4D8] bg-[rgba(0,180,216,0.04)]' : 'border-[rgba(136,150,168,0.2)]'}`}>
              <button type="button" onClick={() => toggle(s)} data-testid={`section-toggle-${s.id}`}
                className="w-full flex items-center gap-2 text-left">
                <span className="w-4 h-4 rounded-sm flex items-center justify-center shrink-0 border"
                  style={{ background: on ? '#00B4D8' : '#fff', borderColor: on ? '#00B4D8' : 'rgba(136,150,168,0.4)' }}>
                  {on && <CheckCircle className="w-3 h-3 text-white" />}
                </span>
                <span className={`text-xs ${on ? 'font-semibold' : ''}`} style={{ color: on ? '#0D1B2A' : '#5C6B7A' }}>{s.label}</span>
              </button>
              {on && s.basis && (
                <p className="mt-1 pl-6 text-[10px] font-mono" style={{ color: '#8896A8' }}>rates {s.basis}</p>
              )}
              {on && s.amount_field && (
                <div className="mt-2 pl-6">
                  <Label className="text-[10px]" style={{ color: '#5C6B7A' }}>{s.amount_label}</Label>
                  <Input type="number" data-testid={`section-amount-${s.id}`} value={form[s.amount_field] ?? ''}
                    onChange={e => setForm(p => ({ ...p, [s.amount_field]: parseFloat(e.target.value) || 0 }))}
                    className="mt-0.5 rounded-sm h-8 text-xs" min="0" />
                </div>
              )}
              {on && (s.fields || []).map(f => (
                <div key={f.name} className="mt-2 pl-6">
                  <Label className="text-[10px]" style={{ color: '#5C6B7A' }}>{f.label}</Label>
                  {f.type === 'select' ? (
                    <select data-testid={`section-field-${f.name}`} value={form[f.name] ?? ''}
                      onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))}
                      className="mt-0.5 w-full rounded-sm h-8 text-xs border px-2 bg-white"
                      style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }}>
                      <option value="">—</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                    </select>
                  ) : (
                    <Input type="number" data-testid={`section-field-${f.name}`} value={form[f.name] ?? ''}
                      onChange={e => setForm(p => ({ ...p, [f.name]: parseFloat(e.target.value) || 0 }))}
                      className="mt-0.5 rounded-sm h-8 text-xs" min="0" />
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SectionBreakdownCard({ sections, gross }) {
  return (
    <Card className="rounded-sm bg-white p-5" data-testid="section-breakdown">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#1A4C7C' }}>Premium by Section</h3>
      <div className="space-y-1.5">
        {sections.map(s => (
          <div key={s.id} className="flex items-center justify-between text-xs" data-testid={`section-premium-${s.id}`}>
            <span style={{ color: '#0D1B2A' }}>{s.section}
              {s.basis && <span className="block text-[10px] font-mono" style={{ color: '#8896A8' }}>{s.basis}</span>}
            </span>
            <span className="flex items-center gap-4">
              <span className="font-mono text-[10px]" style={{ color: '#64748B' }}>{s.amount != null ? fmt$(s.amount) : ''}</span>
              <span className="font-mono font-semibold w-20 text-right" style={{ color: '#0D1B2A' }}>{fmt$(s.premium ?? s.base_premium)}</span>
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between text-xs pt-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
          <span className="font-semibold" style={{ color: '#0D1B2A' }}>Gross premium (after loadings)</span>
          <span className="font-mono font-bold" style={{ color: '#1A4C7C' }}>{fmt$(gross)}</span>
        </div>
      </div>
    </Card>
  );
}

function OutOfAppetiteCard({ className, entity, reason, alternates, onSwitchClass, onChangeEntity }) {
  return (
    <Card className="rounded-sm overflow-hidden border-0" data-testid="out-of-appetite-card">
      <div className="p-5 text-white flex items-center gap-3" style={{ background: '#E24B4A' }}>
        <XCircle className="w-7 h-7 shrink-0" />
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Sans'" }}>Out of Appetite</h2>
          <p className="text-white/85 text-sm">{className} cannot be quoted for {entity.legal_name}</p>
        </div>
      </div>
      <div className="p-5 bg-white space-y-4">
        <p className="text-sm leading-relaxed" style={{ color: '#0D1B2A' }} data-testid="out-of-appetite-reason">{reason}</p>
        {alternates.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#5C6B7A' }}>In appetite for this client</p>
            <div className="flex flex-wrap gap-2">
              {alternates.map(([id, cls]) => (
                <button key={id} type="button" onClick={() => onSwitchClass(id)} data-testid={`appetite-alternate-${id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors hover:bg-[#E8F0FE]"
                  style={{ color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.25)' }}>
                  {cls.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <button type="button" onClick={onChangeEntity} className="text-xs hover:underline" style={{ color: '#1A4C7C' }} data-testid="out-of-appetite-change-entity">
          Change entity
        </button>
      </div>
    </Card>
  );
}
